import type { UserProfile } from '@/types';

/**
 * SKY-507 follow-up: "fresh signup awaiting Skylarq setup".
 *
 * org-absence is NOT a usable signal — ensure_personal_workspace mints a
 * personal org (is_client_workspace=false) on the account's first
 * authenticated request, so every real account has an org within seconds.
 * The durable signals are:
 *   - onboarding_completed=false : every post-2026-03 signup (the old web
 *     onboarding UI that set it true was deleted in 5d7b55bf7; operators can
 *     be grandfathered by flipping the flag),
 *   - not a client workspace     : clients route to the portal proper,
 *   - not an admin               : operators keep the app + admin area.
 * Null profile (load failure) degrades open, matching AuthContext's
 * documented behavior.
 */
export const isAwaitingSetup = (
  userProfile: UserProfile | null | undefined,
  isAdmin: boolean | undefined
): boolean =>
  Boolean(
    userProfile &&
      !userProfile.onboarding_completed &&
      !userProfile.organizations?.is_client_workspace &&
      !isAdmin
  );

/** The company fields a signup gives on ProfileSetup and edits in Settings. */
export interface CompanyProfileFieldValues {
  companyName?: string | null;
  companyWebsite?: string | null;
  role?: string | null;
}

/** The one "signup profile complete" rule: name, website and role all have text. */
export const profileFieldsComplete = (fields: CompanyProfileFieldValues): boolean =>
  Boolean(fields.companyName?.trim() && fields.companyWebsite?.trim() && fields.role?.trim());

/**
 * A profile missing company name, website or role. Says nothing about who is
 * asked for them — callers scope this to accounts awaiting setup.
 */
export const isSignupProfileIncomplete = (userProfile: UserProfile | null | undefined): boolean =>
  Boolean(
    userProfile &&
      !profileFieldsComplete({
        companyName: userProfile.company_name,
        companyWebsite: userProfile.company_website,
        role: userProfile.role,
      })
  );

/** Accounts that live in the portal: client workspace members and fresh signups awaiting setup. */
export const isPortalAccount = (
  userProfile: UserProfile | null | undefined,
  isAdmin: boolean | undefined
): boolean =>
  Boolean(userProfile?.organizations?.is_client_workspace) || isAwaitingSetup(userProfile, isAdmin);

/**
 * An onboarded member of a client workspace lands on the portal home even before
 * any step beyond intake is done — the stepper is honest now, so the "nothing
 * built yet" waitlist would trap them. Both flags are written together by the
 * two paths into a client workspace: approve_signup_workspace_rpc
 * (supabase/migrations/20261018062046_add_signup_approval_rpcs.sql) on signup
 * approval, and invite acceptance (backend/app/services/invite_service.py sets
 * onboarding_completed on claim).
 */
export const skipsSetupGate = (userProfile: UserProfile | null | undefined): boolean =>
  Boolean(userProfile?.onboarding_completed && userProfile.organizations?.is_client_workspace);

/**
 * Gate for a provisioned, not-yet-approved portal account: a live workspace with
 * nothing built beyond intake waits on the setup screen. Unknown step reads
 * (a failed backend read) never show the waitlist.
 */
export const portalGateState = ({
  live,
  stitched,
  stepsUnknown,
}: {
  live: boolean;
  stitched: boolean;
  stepsUnknown: boolean;
}): 'ready' | 'waitlist' => {
  if (stepsUnknown) return 'ready';
  return live && !stitched ? 'waitlist' : 'ready';
};
